// Financial Reporting suite — one produce-and-file card per active series:
// next due period + deadline (status-tinted), "Create report" affordance,
// and the instance history ladder with preview/filed-doc links.

import { Badge, Button, Card, StatusBadge } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import type {
  ReportingDueStatus,
  ReportingSeriesCardDto,
} from "@/api/portfolioAdmin/financialReportingApi";
import type { LoanReportDto } from "@/api/portfolioAdmin/loanReportsApi";
import { LoanReportStatusBadge } from "@/components/portfolioAdmin/loanDetail/reports/LoanReportLedger";
import { formatDate } from "@/components/portfolioAdmin/loanDetail/common/colorBand";
import { reportingPeriodLabel } from "./reportingFormat";

const DUE_TONE: Record<ReportingDueStatus, "danger" | "warn" | "neutral"> = {
  overdue: "danger",
  due: "warn",
  upcoming: "neutral",
};

const DUE_LABEL: Record<ReportingDueStatus, string> = {
  overdue: "Overdue",
  due: "Due soon",
  upcoming: "Upcoming",
};

interface SeriesCardProps {
  card: ReportingSeriesCardDto;
  instances: LoanReportDto[];
  canCreate: boolean;
  isCreating: boolean;
  onCreate: () => void;
  onOpenInstance: (report: LoanReportDto) => void;
}

export function SeriesCard({
  card,
  instances,
  canCreate,
  isCreating,
  onCreate,
  onOpenInstance,
}: SeriesCardProps) {
  const history = instances.slice(0, 6);

  return (
    <Card data-testid={`series-card-${card.code}`}>
      <div className="flex items-start justify-between gap-space-2">
        <div className="min-w-0">
          <div className="flex items-center gap-space-2">
            <span className="text-small font-bold">{card.code}</span>
            <Badge status="info">{card.cadence}</Badge>
          </div>
          <h3 className="text-body font-semibold m-0 mt-space-1 truncate" title={card.title}>
            {card.title}
          </h3>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            {card.audience} · {card.deadlineRule}
          </p>
        </div>
        <span className="shrink-0">
          <StatusBadge tone={DUE_TONE[card.dueStatus]}>
            {DUE_LABEL[card.dueStatus]}
          </StatusBadge>
        </span>
      </div>

      <div className="mt-space-3 flex flex-wrap items-center justify-between gap-space-2">
        <div className="text-small">
          <span className="text-gray-mid">Next period: </span>
          <span className="font-medium" data-testid={`series-next-period-${card.code}`}>
            {reportingPeriodLabel(card.cadence, card.nextPeriodEnd)}
          </span>
          <span className="text-gray-mid"> · due </span>
          <span className="font-medium tabular-nums">
            {formatCalendarDate(card.nextDueDate)}
          </span>
        </div>
        {canCreate && card.hasTemplate && (
          <Button
            variant="primary"
            size="sm"
            className="shrink-0"
            onClick={onCreate}
            loading={isCreating}
            data-testid={`series-create-${card.code}`}
          >
            Create report
          </Button>
        )}
      </div>

      <div className="mt-space-3 border-t border-border-light pt-space-2">
        {history.length === 0 ? (
          <p
            className="text-small text-gray-mid m-0"
            data-testid={`series-history-empty-${card.code}`}
          >
            No reports yet.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-space-1 p-0">
            {history.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-space-2">
                <button
                  type="button"
                  onClick={() => onOpenInstance(r)}
                  className="text-small text-left text-gold-shadow hover:underline underline-offset-4"
                  data-testid={`series-instance-${r.id}`}
                >
                  {reportingPeriodLabel(card.cadence, r.periodEnd)}
                </button>
                <span className="flex items-center gap-space-2 text-caption text-gray-mid">
                  {r.filedAt ? `Filed ${formatDate(r.filedAt)}` : null}
                  <LoanReportStatusBadge status={r.status} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
